import React, { useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Alert,
  Typography,
  TextField,
  Tooltip,
  Stack,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QRCodeSVG } from "qrcode.react";
import {
  startMfaEnrollment,
  confirmMfaEnrollment,
  disableMfa,
} from "../../utils/services/control/mfaService";
import { AuthContext } from "../../context/AuthContext/AuthContext";
import secureStorage from "../../utils/security/secureStorage";

/**
 * Sección de 2FA dentro del perfil del usuario.
 *
 * Estados y diálogos:
 *  - Vista principal: muestra status (activo/inactivo) + botón primario
 *    "Activate 2FA" o "Disable 2FA" según corresponda.
 *  - Enroll dialog: 3 pasos (scan → confirm → codes). El paso "codes" es un
 *    modal NO cerrable hasta que el usuario confirme "I saved them".
 *  - Disable dialog: confirmación simple antes de desactivar.
 */
const MfaSection = () => {
  const { t } = useTranslation();
  const { user, setMfaEnabled } = useContext(AuthContext);
  const mfaEnabled = !!user?.mfaEnabled;

  // Enrollment dialog
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollStep, setEnrollStep] = useState("scan"); // 'scan' | 'codes'
  const [otpAuthUri, setOtpAuthUri] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [enrollError, setEnrollError] = useState(null);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);

  // Disable dialog
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState(null);

  // Snackbar / copy feedback
  const [copyFeedback, setCopyFeedback] = useState(null);

  // ───────────────────── Enrollment ─────────────────────

  const openEnrollment = useCallback(async () => {
    setEnrollError(null);
    setEnrollLoading(true);
    setOtpAuthUri("");
    setTotpCode("");
    setRecoveryCodes([]);
    setCodesAcknowledged(false);
    setEnrollStep("scan");
    setEnrollOpen(true);
    try {
      const data = await startMfaEnrollment();
      if (!data?.success || !data.otpAuthUri) {
        setEnrollError(
          data?.message || t("mfa.errStartEnrollment", "Could not start 2FA enrollment. Try again.")
        );
        return;
      }
      setOtpAuthUri(data.otpAuthUri);
    } catch (err) {
      setEnrollError(t("mfa.errNetworkStart", "Network error starting 2FA enrollment."));
    } finally {
      setEnrollLoading(false);
    }
  }, [t]);

  const handleConfirmTotp = useCallback(async () => {
    setEnrollError(null);
    if (totpCode.length !== 6) {
      setEnrollError(t("mfa.errEnterCode", "Enter the 6-digit code from your authenticator app."));
      return;
    }
    setEnrollLoading(true);
    try {
      const data = await confirmMfaEnrollment(totpCode);
      if (!data?.success || !Array.isArray(data.recoveryCodes)) {
        setEnrollError(data?.message || t("mfa.errInvalidCode", "Invalid code. Please try again."));
        setTotpCode("");
        return;
      }
      setRecoveryCodes(data.recoveryCodes);
      setEnrollStep("codes");
      setMfaEnabled(true);
      // Re-enrollment genera un set nuevo de 8 códigos → limpiar el warning
      // de "pocos recovery codes" si estaba activo.
      secureStorage.removeItem("mfa_low_codes_warning");
    } catch (err) {
      setEnrollError(t("mfa.errNetworkConfirm", "Network error confirming the code."));
    } finally {
      setEnrollLoading(false);
    }
  }, [totpCode, setMfaEnabled, t]);

  const handleFinishEnrollment = () => {
    if (!codesAcknowledged) return;
    setEnrollOpen(false);
  };

  // ───────────────────── Recovery codes actions ─────────────────────

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopyFeedback(t("mfa.copySuccess", "Codes copied to clipboard"));
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch {
      setCopyFeedback(t("mfa.copyFailed", "Copy failed — please select and copy manually"));
      setTimeout(() => setCopyFeedback(null), 3000);
    }
  };

  const downloadCodes = () => {
    const content =
      "MCM Visual — 2FA Recovery Codes\n" +
      "================================\n\n" +
      recoveryCodes.map((c, i) => `${String(i + 1).padStart(2, "0")}. ${c}`).join("\n") +
      "\n\n" +
      "Keep these codes in a safe place. They allow you to sign in if you\n" +
      "lose access to your authenticator app. Each code can only be used ONCE.\n" +
      `Generated: ${new Date().toLocaleString()}\n` +
      `User: ${user?.email || ""}\n`;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mcm-2fa-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printCodes = () => {
    const w = window.open("", "_blank", "width=600,height=700");
    if (!w) return;
    w.document.write(`
      <html>
        <head>
          <title>MCM 2FA Recovery Codes</title>
          <style>
            body { font-family: monospace; padding: 32px; }
            h2 { font-family: sans-serif; }
            .code { font-size: 18px; letter-spacing: 2px; margin: 8px 0; }
            .note { font-family: sans-serif; color: #555; font-size: 12px; margin-top: 24px; }
          </style>
        </head>
        <body>
          <h2>MCM Visual — 2FA Recovery Codes</h2>
          <p>User: ${user?.email || ""}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <hr />
          ${recoveryCodes.map((c, i) => `<div class="code">${i + 1}. ${c}</div>`).join("")}
          <p class="note">
            Keep these codes in a safe place. Each code can only be used ONCE.
            They allow you to sign in if you lose access to your authenticator app.
          </p>
        </body>
      </html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };

  // ───────────────────── Disable ─────────────────────

  const openDisable = () => {
    setDisableError(null);
    setDisableOpen(true);
  };

  const handleDisable = useCallback(async () => {
    if (!user?.employeeId) return;
    setDisableError(null);
    setDisableLoading(true);
    try {
      const data = await disableMfa(user.employeeId);
      if (!data?.success) {
        setDisableError(data?.message || t("mfa.errCannotDisable", "Could not disable 2FA."));
        return;
      }
      setMfaEnabled(false);
      setDisableOpen(false);
    } catch (err) {
      setDisableError(t("mfa.errNetworkDisable", "Network error disabling 2FA."));
    } finally {
      setDisableLoading(false);
    }
  }, [user?.employeeId, setMfaEnabled, t]);

  // ───────────────────── Render ─────────────────────

  return (
    <>
      <Divider />
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          {mfaEnabled ? (
            <ShieldIcon color="success" />
          ) : (
            <ShieldOutlinedIcon color="disabled" />
          )}
          <Typography variant="subtitle1" fontWeight={600}>
            {t("mfa.sectionTitle", "Two-Factor Authentication")}
          </Typography>
          <Chip
            size="small"
            label={
              mfaEnabled
                ? t("mfa.statusActive", "Active")
                : t("mfa.statusInactive", "Inactive")
            }
            color={mfaEnabled ? "success" : "default"}
          />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {mfaEnabled
            ? t("mfa.descActive", "Your account is protected with two-factor authentication. You'll be asked for a verification code when signing in.")
            : t("mfa.descInactive", "Add an extra layer of security. You'll need an authenticator app (Microsoft Authenticator, Google Authenticator, Authy, etc.).")}
        </Typography>

        {mfaEnabled ? (
          <Button
            variant="outlined"
            color="error"
            onClick={openDisable}
            startIcon={<ShieldOutlinedIcon />}
          >
            {t("mfa.disableBtn", "Disable 2FA")}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={openEnrollment}
            startIcon={<ShieldIcon />}
            sx={{
              bgcolor: "#0C343D",
              "&:hover": { bgcolor: "#0a2a33" },
            }}
          >
            {t("mfa.activateBtn", "Activate 2FA")}
          </Button>
        )}
      </Box>

      {/* ──────── Enrollment Dialog ──────── */}
      <Dialog
        open={enrollOpen}
        onClose={
          enrollStep === "codes"
            ? undefined // no cerrable en el paso de códigos
            : () => setEnrollOpen(false)
        }
        disableEscapeKeyDown={enrollStep === "codes"}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#0C343D" }}>
          {enrollStep === "scan"
            ? t("mfa.enrollTitle", "Activate Two-Factor Authentication")
            : t("mfa.saveCodesTitle", "Save Your Recovery Codes")}
        </DialogTitle>

        <DialogContent dividers>
          {enrollStep === "scan" && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                  "mfa.scanInstruction",
                  "Scan this QR code with Microsoft Authenticator, Google Authenticator, Authy or any TOTP-compatible app. Then enter the 6-digit code it generates."
                )}
              </Typography>

              {enrollLoading && !otpAuthUri && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              )}

              {otpAuthUri && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      p: 2,
                      bgcolor: "#fff",
                      border: "1px solid #eee",
                      borderRadius: 2,
                      mb: 2,
                    }}
                  >
                    <QRCodeSVG value={otpAuthUri} size={220} includeMargin />
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {t("mfa.manualCodeLabel", "Can't scan the QR? Use this code manually:")}
                  </Typography>
                  <Box
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      bgcolor: "#f5f5f5",
                      p: 1,
                      borderRadius: 1,
                      mb: 2,
                      wordBreak: "break-all",
                    }}
                  >
                    {otpAuthUri.match(/secret=([A-Z2-7]+)/i)?.[1] || otpAuthUri}
                  </Box>

                  <TextField
                    autoFocus
                    fullWidth
                    label={t("mfa.verificationCodeLabel", "Verification code")}
                    value={totpCode}
                    onChange={(e) =>
                      setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="000000"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 6,
                      style: {
                        textAlign: "center",
                        fontSize: "1.5rem",
                        letterSpacing: "0.4rem",
                        fontFamily: "monospace",
                      },
                    }}
                  />
                </>
              )}

              {enrollError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {enrollError}
                </Alert>
              )}
            </>
          )}

          {enrollStep === "codes" && (
            <>
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{ mb: 2 }}
              >
                {t(
                  "mfa.codesOnceWarning",
                  "These 8 codes will only be shown once. Save them in a safe place (password manager, printed copy, etc.). They let you sign in if you lose access to your phone."
                )}
              </Alert>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                {recoveryCodes.map((code, i) => (
                  <Typography
                    key={code}
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      color: "#0C343D",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}. {code}
                  </Typography>
                ))}
              </Box>

              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Tooltip title={t("mfa.copyTooltip", "Copy all codes")}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={copyCodes}
                  >
                    {t("mfa.copy", "Copy")}
                  </Button>
                </Tooltip>
                <Tooltip title={t("mfa.downloadTooltip", "Download as .txt")}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadCodes}
                  >
                    {t("mfa.download", "Download")}
                  </Button>
                </Tooltip>
                <Tooltip title={t("mfa.printTooltip", "Print")}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={printCodes}
                  >
                    {t("mfa.print", "Print")}
                  </Button>
                </Tooltip>
              </Stack>

              {copyFeedback && (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                  {copyFeedback}
                </Alert>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  id="mfa-ack-checkbox"
                  type="checkbox"
                  checked={codesAcknowledged}
                  onChange={(e) => setCodesAcknowledged(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <label htmlFor="mfa-ack-checkbox" style={{ cursor: "pointer" }}>
                  <Typography variant="body2">
                    {t("mfa.ackSaved", "I have saved these codes in a safe place")}
                  </Typography>
                </label>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {enrollStep === "scan" && (
            <>
              <Button onClick={() => setEnrollOpen(false)} disabled={enrollLoading}>
                {t("mfa.cancel", "Cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmTotp}
                disabled={totpCode.length !== 6 || enrollLoading || !otpAuthUri}
                sx={{ bgcolor: "#0C343D", "&:hover": { bgcolor: "#0a2a33" } }}
              >
                {enrollLoading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  t("mfa.confirmActivate", "Confirm & Activate")
                )}
              </Button>
            </>
          )}
          {enrollStep === "codes" && (
            <Button
              variant="contained"
              onClick={handleFinishEnrollment}
              disabled={!codesAcknowledged}
              sx={{ bgcolor: "#0C343D", "&:hover": { bgcolor: "#0a2a33" } }}
            >
              {t("mfa.savedContinue", "I saved them, continue")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ──────── Disable Dialog ──────── */}
      <Dialog open={disableOpen} onClose={() => !disableLoading && setDisableOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("mfa.disableDialogTitle", "Disable Two-Factor Authentication?")}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("mfa.disableWarning", "Your account will be less secure. You'll only need your password to sign in.")}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t(
              "mfa.disableConsequence",
              "All your recovery codes will be deleted. If you want to re-enable 2FA later you'll need to scan a new QR and get new recovery codes."
            )}
          </Typography>
          {disableError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {disableError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDisableOpen(false)} disabled={disableLoading}>
            {t("mfa.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisable}
            disabled={disableLoading}
          >
            {disableLoading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("mfa.disableBtn", "Disable 2FA")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MfaSection;
